import { useCallback, useEffect, useState } from "react";
import { Shield, ShieldOff, Trash2, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import { useAuth } from "@/hooks/useAuth";
import type {
  GenericResponse,
  UpdateUserRoleRequest,
  UserCreateRequest,
  UserRole,
  UserWithRole,
} from "@/lib/types";

export function UsersPage() {
  const { username: currentUser } = useAuth();
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>("admin");
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get<{ users: UserWithRole[] }>("/api/users");
      setUsers((resp.users ?? []).slice().sort((a, b) => a.username.localeCompare(b.username)));
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const u = username.trim();
    const p = password.trim();
    if (!u || !p) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      const body: UserCreateRequest = { username: u, password: p, role };
      const resp = await api.post<GenericResponse>("/api/users", body);
      if (resp.success) {
        setFeedback({
          kind: "success",
          text: t("Benutzer „{name}\" ({role}) angelegt.", { name: u, role: roleLabel(role) }),
        });
        setUsername("");
        setPassword("");
        setRole("admin");
        await reload();
      } else {
        setFeedback({ kind: "error", text: resp.message });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Anlegen fehlgeschlagen") });
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (name: string) => {
    if (!confirm(t("Benutzer „{name}\" wirklich löschen?", { name }))) return;
    try {
      const resp = await api.delete<GenericResponse>(`/api/users/${encodeURIComponent(name)}`);
      if (resp.success === false) {
        setFeedback({ kind: "error", text: resp.message });
        return;
      }
      setFeedback({ kind: "success", text: t("Benutzer „{name}\" gelöscht.", { name }) });
      await reload();
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Löschen fehlgeschlagen") });
    }
  };

  const onChangeRole = async (name: string, newRole: UserRole) => {
    const question =
      newRole === "admin"
        ? t("Benutzer „{name}\" zum Admin befördern?", { name })
        : t("Benutzer „{name}\" auf Viewer zurückstufen?", { name });
    if (!confirm(question)) return;
    try {
      const body: UpdateUserRoleRequest = { role: newRole };
      const resp = await api.patch<GenericResponse>(
        `/api/users/${encodeURIComponent(name)}/role`,
        body,
      );
      if (resp.success === false) {
        setFeedback({ kind: "error", text: resp.message });
        return;
      }
      setFeedback({ kind: "success", text: t("„{name}\" ist jetzt {role}.", { name, role: roleLabel(newRole) }) });
      await reload();
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Rollen-Wechsel fehlgeschlagen") });
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      <Card
        title={t("Benutzer anlegen")}
        description={t("Admins verwalten Konfiguration; Viewer sehen Logs, Traces und Anlagen-Live.")}
      >
        {/* lg statt md: unterhalb ~1024px (Sidebar frisst 220px) würden die
            vier Spalten überlaufen und den Anlegen-Button abschneiden. */}
        <form onSubmit={onCreate} className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_1fr_140px_auto]">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("Benutzername")}
            </span>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="off"
              className="h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("Passwort")}
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              className="h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("Rolle")}
            </span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            >
              <option value="admin">{t("Admin")}</option>
              <option value="viewer">{t("Viewer")}</option>
            </select>
          </label>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex h-9 items-center gap-1.5 self-end rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
          >
            <UserPlus className="h-3.5 w-3.5" /> {submitting ? t("Anlegen…") : t("Anlegen")}
          </button>
        </form>
      </Card>

      <Card title={t("Bestehende Benutzer ({n})", { n: users.length })}>
        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : users.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {t("Noch keine Benutzer angelegt.")}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {users.map((u) => {
              const isSelf = u.username === currentUser;
              return (
                <li
                  key={u.username}
                  // flex-wrap: auf dem Handy rutschen die Aktionen unter Name/Badges.
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-2.5 text-sm first:pt-0 last:pb-0"
                >
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="font-mono text-xs">{u.username}</span>
                    <RoleBadge role={u.role} />
                    {isSelf ? (
                      <span className="rounded-full bg-accent px-1.5 py-0.5 text-[10px] text-accent-foreground">
                        {t("Du")}
                      </span>
                    ) : null}
                  </div>
                  <div className="ml-auto flex items-center gap-1">
                    {u.role === "viewer" ? (
                      <button
                        type="button"
                        onClick={() => onChangeRole(u.username, "admin")}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
                      >
                        <Shield className="h-3.5 w-3.5" /> {t("Zu Admin")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onChangeRole(u.username, "viewer")}
                        disabled={isSelf}
                        title={isSelf ? t("Eigene Rolle kann nicht gesenkt werden") : undefined}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <ShieldOff className="h-3.5 w-3.5" /> {t("Zu Viewer")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onDelete(u.username)}
                      disabled={isSelf}
                      title={isSelf ? t("Eigenen Account kann man nicht löschen") : undefined}
                      aria-label={t("Benutzer {name} löschen", { name: u.username })}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> {t("Löschen")}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}

function RoleBadge({ role }: { role: UserRole }) {
  if (role === "admin") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary">
        <Shield className="h-3 w-3" /> {t("Admin")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
      <ShieldOff className="h-3 w-3" /> {t("Viewer")}
    </span>
  );
}

function roleLabel(role: UserRole): string {
  return role === "admin" ? t("Admin") : t("Viewer");
}
